import { ReactNode } from 'react';
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Person } from '@mui/icons-material';
import { usePopupElement } from '@shared/hooks';
import { LogOutIcon } from '@shared/custom-icons';
import { useSharedTranslation } from '@shared/i18n';
import { Paper } from '../Block';
import { SidebarUserAvatar } from './SidebarUserAvatar';

export type SidebarMenuProps = {
  userName: string;
  userPicture?: string;
  jobTitle?: string;
  expandedView: boolean;
  logOut: () => void;
  UserMenuExpanded?: (
    props: Omit<SidebarMenuProps, 'expandedView' | 'CustomExpandedUserMenu'>
  ) => JSX.Element;
  UserMenuCollapsed?: (
    props: Omit<SidebarMenuProps, 'expandedView' | 'CustomCollapsedUserMenu'>
  ) => JSX.Element;
};

export function SidebarUserMenu({
  expandedView,
  UserMenuExpanded = ExpandedUserMenu,
  UserMenuCollapsed = CollapsedUserMenu,
  ...props
}: SidebarMenuProps) {
  return expandedView ? <UserMenuExpanded {...props} /> : <UserMenuCollapsed {...props} />;
}

export function ExpandedUserMenu({
  userName,
  userPicture,
  jobTitle,
  logOut,
}: Omit<SidebarMenuProps, 'expandedView'>) {
  const { t } = useSharedTranslation();

  return (
    <Paper
      sx={{
        width: '100%',
        padding: 1.5,
        backgroundColor: 'background.default',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ borderRadius: 6, backgroundColor: 'background.paper' }}
      >
        <Stack direction="row" alignItems="center">
          <SidebarUserAvatar username={userName} userPicture={userPicture} />

          <Box ml={0.5} display="flex" flexDirection="column">
            <Typography sx={{ fontWeight: 700 }}>{userName}</Typography>
            {jobTitle && (
              <Typography
                variant="body3"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {jobTitle}
              </Typography>
            )}
          </Box>
        </Stack>

        <Tooltip title={t('shared.logOut')} arrow placement="right">
          <IconButton onClick={logOut} sx={{ mr: 1, color: 'grey.A400' }}>
            <LogOutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Paper>
  );
}

export function CollapsedUserMenu({
  userName,
  userPicture,
  children,
  logOut,
}: Omit<SidebarMenuProps, 'expandedView'> & { children?: ReactNode }) {
  const { t } = useSharedTranslation();
  const { menuElementProps, triggerElementProps } = usePopupElement();

  return (
    <Stack>
      {children}

      <IconButton {...triggerElementProps} sx={{ m: 0, p: 0 }}>
        <SidebarUserAvatar username={userName} userPicture={userPicture} />
      </IconButton>

      <Popover
        {...menuElementProps}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        elevation={0}
        sx={{ '& .MuiPaper-root': { padding: 1 } }}
      >
        <List sx={{ padding: 0, color: 'text.secondary' }}>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 0, mr: 2 }}>
              <Person fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={userName}
              primaryTypographyProps={{
                variant: 'body2',
              }}
            />
          </ListItem>

          <ListItemButton onClick={logOut}>
            <ListItemIcon sx={{ minWidth: 0, mr: 2 }}>
              <LogOutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t('shared.logOut')}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItemButton>
        </List>
      </Popover>
    </Stack>
  );
}
